/**
 * Atlas ↔ onboardingState adapter.
 *
 * Reads: still hit `localStorage` via the helpers in
 * `shared/onboarding/onboardingState.ts`. The legacy localStorage keys are
 * kept in sync by `OnboardingStateProvider`, which seeds them from the server
 * GET and from Supabase Realtime, so other-user progress is visible without
 * making the adapter async.
 *
 * Writes: dual-path. They update localStorage synchronously (so the next read
 * on the same tab is consistent) AND call the `onboardingStateBridge` to
 * PATCH the server. When no provider is mounted (tests, SSR), the bridge
 * silently no-ops and the code degrades to the pre-PR2 behaviour.
 *
 * Welcome tour and atlas-dock-dismiss stay per-user in localStorage — they
 * are UI preferences, not progress. See PR2 plan.
 */
import {
  TERMINAL_COMPLETED,
  TERMINAL_SKIPPED,
  type AtlasMission,
  type AtlasMissionId as _AtlasMissionId,
  type AtlasMissionState,
} from '../atlas.types';
import {
  advanceForecastTourStep,
  advancePostUpdateJourneyStepForProduct,
  advanceReleasesTourStep,
  clearAtlasMissionStep,
  clearAtlasSnooze,
  forecastTourStorageKey,
  getAtlasMissionStep,
  getAtlasSnoozeUntil,
  getForecastTourStep,
  getPostUpdateJourneyStepForProduct,
  getReleasesTourStep,
  hasFirstBaselineRecorded,
  hasFirstReportExported,
  isFeatureCoachCompletedForProduct,
  isFeatureCoachSuppressAll,
  isWelcomeTourCompleted,
  markFeatureCoachCompletedForProduct,
  markFeatureCoachSuppressAll,
  markFirstBaselineRecorded,
  markFirstReportExported,
  markWelcomeTourCompleted,
  releasesTourStorageKey,
  retreatForecastTourStep,
  retreatReleasesTourStep,
  setAtlasMissionStep,
  setAtlasSnoozeUntil,
  setForecastTourStep,
  setPostUpdateJourneyStepForProduct,
  setReleasesTourStep,
} from '../../../shared/onboarding/onboardingState';
import {
  publishClearUserOverride,
  publishFlag,
  publishMissionStep,
} from '../../../shared/onboarding/onboardingStateBridge';
import { ATLAS_MISSIONS } from '../missions';

/** Atlas mission id -> canonical machine key for the server bridge. */
function machineKeyForMission(missionId: string): string {
  return `mission.${missionId === 'post-update-journey' ? 'postUpdateJourney' : missionId}`;
}

/** 24h snooze window. */
export const ATLAS_SNOOZE_MS = 24 * 60 * 60 * 1000;

function isTerminal(step: string | undefined): step is 'completed' | 'skipped' {
  return step === TERMINAL_COMPLETED || step === TERMINAL_SKIPPED;
}

/**
 * Flags synthesized by the provider when calling `readMissionState` so derived
 * missions (`storage: 'derived'`) can resolve their completion without
 * touching localStorage. Keep this minimal and additive.
 *
 * Beyond the derived-storage missions, the calendar-connect mission (which
 * uses `storage: 'atlas'` for its tour step machine) also consumes these
 * flags to compute `completed` as the AND of five real checkpoints — see
 * `readMissionState` special case below.
 */
export interface DerivedCompletionFlags {
  hasOrg: boolean;
  hasProduct: boolean;
  hasSharePoint?: boolean;
  hasCalendarSynced?: boolean;
  hasOpenedAbsenceKeywords?: boolean;
  hasOpenedPublicHolidayKeywords?: boolean;
  hasVisitedReleases?: boolean;
}

/** Reads the raw stored step for a mission (may be terminal). */
export function readMissionStep(
  mission: AtlasMission,
  orgId: string,
  productId: string,
  derived?: DerivedCompletionFlags
): string | undefined {
  switch (mission.storage) {
    case 'welcome':
      return isWelcomeTourCompleted() ? TERMINAL_COMPLETED : undefined;
    case 'forecast':
      return getForecastTourStep(orgId, productId);
    case 'releases':
      return getReleasesTourStep(orgId, productId);
    case 'journey':
      return getPostUpdateJourneyStepForProduct(orgId, productId);
    case 'derived': {
      if (!derived) return undefined;
      if (mission.id === 'create-organization') {
        return derived.hasOrg ? TERMINAL_COMPLETED : undefined;
      }
      if (mission.id === 'add-first-product') {
        return derived.hasProduct ? TERMINAL_COMPLETED : undefined;
      }
      return undefined;
    }
    case 'atlas':
    default:
      return getAtlasMissionStep(mission.id, orgId, productId);
  }
}

export function writeMissionStep(
  mission: AtlasMission,
  orgId: string,
  productId: string,
  step: string
): void {
  switch (mission.storage) {
    case 'welcome':
      // Welcome is explicitly per-user — never published to the org-wide table.
      if (step === TERMINAL_COMPLETED || step === TERMINAL_SKIPPED) {
        markWelcomeTourCompleted();
      }
      return;
    case 'forecast':
      setForecastTourStep(orgId, productId, step as never);
      publishMissionStep(machineKeyForMission('forecast-tour'), step);
      return;
    case 'releases':
      setReleasesTourStep(orgId, productId, step as never);
      publishMissionStep(machineKeyForMission('releases-tour'), step);
      return;
    case 'journey':
      setPostUpdateJourneyStepForProduct(orgId, productId, step as never);
      publishMissionStep('mission.postUpdateJourney', step);
      return;
    case 'derived':
      // Derived missions are not writable — completion is computed from
      // AtlasContext (see `readMissionStep`). Silently ignore writes so
      // `skipMission` and `completeMission` remain safe no-ops.
      return;
    case 'atlas':
    default:
      setAtlasMissionStep(mission.id, orgId, productId, step);
      publishMissionStep(`mission.${mission.id}`, step);
      return;
  }
}

export function advanceMissionStep(
  mission: AtlasMission,
  orgId: string,
  productId: string
): string | undefined {
  if (mission.storage === 'forecast') {
    advanceForecastTourStep(orgId, productId);
    const next = getForecastTourStep(orgId, productId);
    if (next) publishMissionStep(machineKeyForMission('forecast-tour'), next);
    if (next === TERMINAL_COMPLETED) {
      markFeatureCoachCompletedForProduct(mission.featureKey, productId);
      publishFlag(`coach.${mission.featureKey}.completed`);
    }
    return next;
  }
  if (mission.storage === 'releases') {
    advanceReleasesTourStep(orgId, productId);
    const next = getReleasesTourStep(orgId, productId);
    if (next) publishMissionStep(machineKeyForMission('releases-tour'), next);
    if (next === TERMINAL_COMPLETED) {
      // Fix the legacy featureKey split: ReleasesPage uses `'releasesTour'` for
      // the card and `'releases'` for the completion gate. Atlas writes BOTH
      // so new and old code agree during the grace period.
      markFeatureCoachCompletedForProduct(mission.featureKey, productId); // 'releases'
      markFeatureCoachCompletedForProduct('releasesTour', productId);
      publishFlag(`coach.${mission.featureKey}.completed`);
      publishFlag('coach.releasesTour.completed');
    }
    return next;
  }
  if (mission.storage === 'journey') {
    advancePostUpdateJourneyStepForProduct(orgId, productId);
    const next = getPostUpdateJourneyStepForProduct(orgId, productId);
    if (next) publishMissionStep('mission.postUpdateJourney', next);
    return next;
  }
  // Atlas-native missions: compute next index against mission.steps
  const current = readMissionStep(mission, orgId, productId);
  if (isTerminal(current)) return current;
  const stepIds = mission.steps.map((s) => s.id);
  const idx = current ? stepIds.indexOf(current) : -1;
  if (idx === -1 && stepIds.length > 0) {
    writeMissionStep(mission, orgId, productId, stepIds[0]);
    return stepIds[0];
  }
  if (idx >= stepIds.length - 1) {
    writeMissionStep(mission, orgId, productId, TERMINAL_COMPLETED);
    // Legacy parity: also set the featureKey-completed flag so existing code that
    // gates on `isFeatureCoachCompletedForProduct(featureKey, productId)` agrees.
    markFeatureCoachCompletedForProduct(mission.featureKey, productId);
    publishFlag(`coach.${mission.featureKey}.completed`);
    return TERMINAL_COMPLETED;
  }
  const next = stepIds[idx + 1];
  writeMissionStep(mission, orgId, productId, next);
  return next;
}

export function retreatMissionStep(
  mission: AtlasMission,
  orgId: string,
  productId: string
): string | undefined {
  if (mission.storage === 'forecast') {
    retreatForecastTourStep(orgId, productId);
    const next = getForecastTourStep(orgId, productId);
    // Retreat is a legit user action, but the server enforces max-ordinal
    // semantics on `org-product` — sending a lower ordinal will be ignored.
    // Treat retreat as a personal override so the user's local view rewinds
    // without rolling back the org-wide canonical state.
    if (next) publishMissionStep(machineKeyForMission('forecast-tour'), next, 'user');
    return next;
  }
  if (mission.storage === 'releases') {
    retreatReleasesTourStep(orgId, productId);
    const next = getReleasesTourStep(orgId, productId);
    if (next) publishMissionStep(machineKeyForMission('releases-tour'), next, 'user');
    return next;
  }
  // Atlas-native: walk backwards
  const current = readMissionStep(mission, orgId, productId);
  if (isTerminal(current) || !current) return current;
  const stepIds = mission.steps.map((s) => s.id);
  const idx = stepIds.indexOf(current);
  if (idx <= 0) return current;
  const prev = stepIds[idx - 1];
  writeMissionStep(mission, orgId, productId, prev);
  return prev;
}

export function restartMission(
  mission: AtlasMission,
  orgId: string,
  productId: string
): string | undefined {
  if (mission.steps.length === 0) return undefined;
  const first = mission.steps[0].id;
  writeMissionStep(mission, orgId, productId, first);
  return first;
}

export function skipMission(mission: AtlasMission, orgId: string, productId: string): void {
  writeMissionStep(mission, orgId, productId, TERMINAL_SKIPPED);
}

export function snoozeMission(
  mission: AtlasMission,
  orgId: string,
  productId: string,
  durationMs: number = ATLAS_SNOOZE_MS
): void {
  setAtlasSnoozeUntil(mission.id, orgId, productId, Date.now() + durationMs);
}

export function clearMissionSnooze(mission: AtlasMission, orgId: string, productId: string): void {
  clearAtlasSnooze(mission.id, orgId, productId);
}

export function restartFromScratch(mission: AtlasMission, orgId: string, productId: string): void {
  if (mission.storage === 'atlas') {
    clearAtlasMissionStep(mission.id, orgId, productId);
  }
  clearMissionSnooze(mission, orgId, productId);
  // Personal restart — wipe this user's overlay so they see the tour from
  // step 0, without touching the org-wide canonical snapshot. If the user had
  // any prior `scope='user'` mission writes (e.g. from a previous retreat),
  // they are cleared here so the org-wide state takes over again.
  publishClearUserOverride();
}

export function readMissionState(
  mission: AtlasMission,
  orgId: string,
  productId: string,
  derived?: DerivedCompletionFlags
): AtlasMissionState {
  const step = readMissionStep(mission, orgId, productId, derived);
  const snoozedUntil = getAtlasSnoozeUntil(mission.id, orgId, productId);

  // calendar-connect: step machine still drives the on-page guided tour, but
  // the Atlas Dock's Done pill is gated on real user actions (see
  // DerivedCompletionFlags comment). If all five checkpoints are set we mark
  // completed regardless of the tour step; if Skip was used we honor that.
  if (mission.id === 'calendar-connect' && derived) {
    const allChecks =
      derived.hasSharePoint === true &&
      derived.hasCalendarSynced === true &&
      derived.hasOpenedAbsenceKeywords === true &&
      derived.hasOpenedPublicHolidayKeywords === true &&
      derived.hasVisitedReleases === true;
    return {
      missionId: mission.id,
      step,
      completed: allChecks,
      skipped: step === TERMINAL_SKIPPED,
      snoozedUntil,
    };
  }

  // A mission is considered completed when either:
  //  - the step machine stored `'completed'`
  //  - the FeatureCoach completion flag is set for this featureKey + product
  //  - global suppress-all for this featureKey is set
  let coachCompleted =
    mission.featureKey !== '' &&
    (isFeatureCoachCompletedForProduct(mission.featureKey, productId) ||
      isFeatureCoachSuppressAll(mission.featureKey));
  // Releases split-key compat: check the legacy `'releasesTour'` flag too.
  if (!coachCompleted && mission.storage === 'releases') {
    coachCompleted =
      isFeatureCoachCompletedForProduct('releasesTour', productId) ||
      isFeatureCoachSuppressAll('releasesTour');
  }
  return {
    missionId: mission.id,
    step,
    completed: step === TERMINAL_COMPLETED || coachCompleted,
    skipped: step === TERMINAL_SKIPPED,
    snoozedUntil,
  };
}

/** Marks the mission as fully complete via BOTH the step machine and legacy feature flag. */
export function completeMission(mission: AtlasMission, orgId: string, productId: string): void {
  writeMissionStep(mission, orgId, productId, TERMINAL_COMPLETED);
  if (mission.featureKey) {
    markFeatureCoachCompletedForProduct(mission.featureKey, productId);
    publishFlag(`coach.${mission.featureKey}.completed`);
  }
  // Releases split-key compat: also stamp the legacy `'releasesTour'` key.
  if (mission.storage === 'releases') {
    markFeatureCoachCompletedForProduct('releasesTour', productId);
    publishFlag('coach.releasesTour.completed');
  }
}

export function suppressMissionForAllProducts(mission: AtlasMission): void {
  if (!mission.featureKey) return;
  markFeatureCoachSuppressAll(mission.featureKey);
  // `suppressAll` is org-wide by definition — publish to scope='org'.
  publishFlag(`coach.${mission.featureKey}.suppressAll`, 'org');
}

/**
 * Clears every `skipped` terminal for this org + product so Atlas missions show
 * in the dock again (after the user used Skip on a mission).
 */
export function restoreAtlasNavigatorState(orgId: string, productId: string): void {
  for (const mission of ATLAS_MISSIONS) {
    if (mission.id === 'welcome' || mission.storage === 'derived') continue;

    const step = readMissionStep(mission, orgId, productId);
    if (step !== TERMINAL_SKIPPED) continue;

    switch (mission.storage) {
      case 'forecast':
        try {
          localStorage.removeItem(forecastTourStorageKey(orgId, productId));
        } catch {
          // ignore
        }
        break;
      case 'releases':
        try {
          localStorage.removeItem(releasesTourStorageKey(orgId, productId));
        } catch {
          // ignore
        }
        break;
      case 'journey':
        setPostUpdateJourneyStepForProduct(orgId, productId, 'dateFilter');
        break;
      case 'atlas':
        clearAtlasMissionStep(mission.id, orgId, productId);
        clearAtlasSnooze(mission.id, orgId, productId);
        break;
      default:
        break;
    }
  }
}

// Re-export the read-side checkpoint helpers — purely synchronous, still
// backed by localStorage (kept in sync by OnboardingStateProvider).
export { hasFirstReportExported, hasFirstBaselineRecorded };

// Write-side checkpoint wrappers publish the flag to the server in addition
// to stamping localStorage, so other members of the org see the checkpoint
// as completed too.
export function markFirstReportExportedAndPublish(orgId: string, productId: string): void {
  markFirstReportExported(orgId, productId);
  publishFlag('checkpoint.firstReport');
}

export function markFirstBaselineRecordedAndPublish(orgId: string, productId: string): void {
  markFirstBaselineRecorded(orgId, productId);
  publishFlag('checkpoint.firstBaseline');
}

// Legacy names still exported for backward compatibility — these now also
// publish through the bridge so Atlas callers don't need to be updated.
export { markFirstReportExportedAndPublish as markFirstReportExported };
export { markFirstBaselineRecordedAndPublish as markFirstBaselineRecorded };
